class PjBulkCompareView extends HTMLElement {
  connectedCallback() {
    if (this.pjBulkCompareInitialized) return;

    this.pjBulkCompareInitialized = true;
    this.handleClick = this.handleClick.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleSectionContentUpdated = this.handleSectionContentUpdated.bind(this);
    this.handleTableScroll = this.handleTableScroll.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.showPhoto = this.showPhoto.bind(this);
    this.hidePhoto = this.hidePhoto.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerover', this.showPhoto);
    this.addEventListener('pointerout', this.hidePhoto);
    this.addEventListener('focusin', this.showPhoto);
    this.addEventListener('focusout', this.hidePhoto);
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('scroll', this.handleWindowResize, { passive: true });
    document.addEventListener('section:content-updated', this.handleSectionContentUpdated);
    this.refresh();
  }

  refresh() {
    const firstRefresh = this.dataset.pjBulkCompareReady !== 'true';
    const currentMode = this.dataset.mode === 'compare' ? 'compare' : 'grid';
    this.switcher = this.querySelector('[data-pj-bulk-view-switcher]');
    this.grid = this.querySelector('[data-pj-bulk-grid]');
    this.table = this.querySelector('[data-pj-bulk-table]');
    this.status = this.querySelector('[data-pj-bulk-view-status]');
    this.buttons = [...this.querySelectorAll('.pj-bulk-compare__switch [data-pj-bulk-mode]')];
    this.compareButton = this.querySelector('.pj-bulk-compare__switch [data-pj-bulk-mode="compare"]');
    this.intro = this.querySelector('[data-pj-bulk-compare-intro]');
    this.photoFlyout = this.querySelector('[data-pj-bulk-photo-flyout]');
    const nextScrollArea = this.querySelector('[data-pj-bulk-scroll]');
    if (this.scrollArea && this.scrollArea !== nextScrollArea) {
      this.scrollArea.removeEventListener('scroll', this.handleTableScroll);
    }
    this.scrollArea = nextScrollArea;
    this.stickyHead = this.querySelector('[data-pj-bulk-sticky-head]');
    this.stickyHeadViewport = this.querySelector('[data-pj-bulk-sticky-head-viewport]');

    if (!this.switcher || !this.grid || !this.table || this.buttons.length !== 2) return;

    this.dataset.pjBulkCompareReady = 'true';
    this.switcher.hidden = false;
    this.setMode(firstRefresh ? 'grid' : currentMode, { updateUrl: firstRefresh, announce: false });
    if (firstRefresh) this.maybeShowIntro();
    this.setupStickyHeader();
    if (this.activeSort) this.applySort({ announce: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerover', this.showPhoto);
    this.removeEventListener('pointerout', this.hidePhoto);
    this.removeEventListener('focusin', this.showPhoto);
    this.removeEventListener('focusout', this.hidePhoto);
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('scroll', this.handleWindowResize);
    document.removeEventListener('section:content-updated', this.handleSectionContentUpdated);
    this.scrollArea?.removeEventListener('scroll', this.handleTableScroll);
    this.pjBulkCompareInitialized = false;
  }

  initialMode() {
    return 'grid';
  }

  maybeShowIntro() {
    if (!this.intro || !this.compareButton || this.introPresented) return;

    const storageKey = this.dataset.introStorageKey;
    let alreadySeen = false;
    try {
      alreadySeen = window.localStorage.getItem(storageKey) === 'true';
    } catch (_) {
      // The tip still works when storage is unavailable; it simply cannot persist.
    }
    if (alreadySeen) return;

    this.introPresented = true;
    this.intro.hidden = false;
    this.compareButton.setAttribute('data-pj-bulk-intro-highlight', '');
    try {
      window.localStorage.setItem(storageKey, 'true');
    } catch (_) {
      // A blocked storage API must never block the collection.
    }
  }

  dismissIntro() {
    if (this.intro) this.intro.hidden = true;
    this.compareButton?.removeAttribute('data-pj-bulk-intro-highlight');
  }

  handleClick(event) {
    const dismissButton = event.target.closest('[data-pj-bulk-intro-dismiss]');
    if (dismissButton && this.contains(dismissButton)) {
      this.dismissIntro();
      return;
    }

    const sortButton = event.target.closest('[data-pj-bulk-sort]');
    if (sortButton && this.contains(sortButton)) {
      this.sortBy(sortButton);
      return;
    }

    const button = event.target.closest('[data-pj-bulk-mode]');
    if (!button || !this.contains(button)) return;
    if (button.dataset.pjBulkMode === 'compare') this.dismissIntro();
    this.setMode(button.dataset.pjBulkMode, { updateUrl: true, announce: true });
  }

  sortBy(button) {
    const key = button.dataset.pjBulkSort;
    const type = button.dataset.sortType || 'text';
    const direction = this.activeSort?.key === key && this.activeSort.direction === 'ascending'
      ? 'descending'
      : 'ascending';

    this.activeSort = {
      key,
      type,
      direction,
      label: button.dataset.sortLabel || button.textContent.trim(),
    };
    this.applySort({ announce: true });
  }

  showPhoto(event) {
    const link = event.target.closest('[data-pj-bulk-photo]');
    if (!link || !this.contains(link) || !this.photoFlyout) return;

    const image = this.photoFlyout.querySelector('[data-pj-bulk-photo-image]');
    const title = this.photoFlyout.querySelector('[data-pj-bulk-photo-title]');
    if (!image || !title || !link.dataset.photoSrc) return;

    image.src = link.dataset.photoSrc;
    image.srcset = link.dataset.photoSrcset || '';
    image.sizes = '206px';
    title.textContent = link.dataset.photoTitle || link.textContent.trim();

    const cellRect = link.closest('.pj-bulk-compare__strain')?.getBoundingClientRect() || link.getBoundingClientRect();
    const flyoutWidth = 222;
    const flyoutHeight = 248;
    const gap = 12;
    const left = Math.min(cellRect.right + gap, window.innerWidth - flyoutWidth - gap);
    const centeredTop = cellRect.top + (cellRect.height - flyoutHeight) / 2;
    const top = Math.max(gap, Math.min(centeredTop, window.innerHeight - flyoutHeight - gap));

    this.photoFlyout.style.left = `${Math.max(gap, left)}px`;
    this.photoFlyout.style.top = `${top}px`;
    this.photoFlyout.hidden = false;
  }

  hidePhoto(event) {
    const link = event.target.closest('[data-pj-bulk-photo]');
    if (!link || !this.photoFlyout) return;
    if (event.relatedTarget instanceof Node && link.contains(event.relatedTarget)) return;
    if (link.matches(':hover, :focus')) return;
    this.photoFlyout.hidden = true;
  }

  applySort({ announce }) {
    if (!this.activeSort) return;

    const tbody = this.querySelector('.pj-bulk-compare__table tbody');
    const { key, type, direction, label } = this.activeSort;
    const currentButtons = [...this.querySelectorAll(`[data-pj-bulk-sort="${key}"]`)];
    if (!tbody || currentButtons.length === 0) return;

    const entries = [...tbody.children].map((row, index) => ({ row, index }));
    const valueFor = (row) => {
      const cell = row.querySelector(`[data-sort-key="${key}"]`);
      if (!cell) return '';
      if (key === 'quantity') return cell.querySelector('input[name="quantity"]')?.value || cell.dataset.sortValue || '';
      return cell.dataset.sortValue || '';
    };

    entries.sort((a, b) => {
      const rawA = valueFor(a.row);
      const rawB = valueFor(b.row);
      const blankA = rawA === '';
      const blankB = rawB === '';
      if (blankA !== blankB) return blankA ? 1 : -1;
      if (blankA && blankB) return a.index - b.index;

      let comparison = 0;
      if (type === 'number') {
        comparison = Number.parseFloat(rawA) - Number.parseFloat(rawB);
      } else if (type === 'date') {
        comparison = Date.parse(rawA) - Date.parse(rawB);
      } else {
        comparison = rawA.localeCompare(rawB, undefined, { numeric: true, sensitivity: 'base' });
      }

      if (Number.isNaN(comparison) || comparison === 0) return a.index - b.index;
      return direction === 'ascending' ? comparison : -comparison;
    });

    tbody.append(...entries.map(({ row }) => row));
    this.querySelectorAll('.pj-bulk-compare__table thead th[aria-sort]').forEach((heading) => {
      heading.setAttribute('aria-sort', 'none');
    });
    currentButtons.forEach((button) => button.closest('th')?.setAttribute('aria-sort', direction));

    if (announce && this.status) {
      this.status.textContent = `${label} sorted ${direction}.`;
    }
  }

  handlePopState() {
    this.setMode('grid', { updateUrl: true, announce: false });
  }

  handleSectionContentUpdated() {
    window.requestAnimationFrame(() => {
      if (this.isConnected) this.refresh();
    });
  }

  setupStickyHeader() {
    if (!this.scrollArea || !this.stickyHead || !this.stickyHeadViewport) return;

    this.scrollArea.removeEventListener('scroll', this.handleTableScroll);
    this.scrollArea.addEventListener('scroll', this.handleTableScroll, { passive: true });
    this.stickyHeadViewport.replaceChildren();

    const sourceTable = this.scrollArea.querySelector('.pj-bulk-compare__table');
    const sourceHead = sourceTable?.querySelector('thead');
    if (!sourceTable || !sourceHead) return;

    const stickyTable = document.createElement('table');
    stickyTable.className = 'pj-bulk-compare__table pj-bulk-compare__table--sticky';
    stickyTable.setAttribute('aria-hidden', 'true');
    stickyTable.append(sourceHead.cloneNode(true));
    stickyTable.querySelectorAll('button').forEach((button) => button.setAttribute('tabindex', '-1'));
    this.stickyHeadViewport.append(stickyTable);
    this.stickyTable = stickyTable;

    window.requestAnimationFrame(() => this.syncStickyHeader());
  }

  handleTableScroll() {
    this.syncStickyHeader();
  }

  handleWindowResize() {
    window.requestAnimationFrame(() => this.syncStickyHeader());
  }

  syncStickyHeader() {
    if (!this.scrollArea || !this.stickyHeadViewport || !this.stickyTable || this.dataset.mode !== 'compare') return;

    const sourceTable = this.scrollArea.querySelector('.pj-bulk-compare__table');
    const sourceHeaders = [...(sourceTable?.querySelectorAll('thead th') || [])];
    const stickyHeaders = [...this.stickyTable.querySelectorAll('thead th')];
    if (!sourceTable || sourceHeaders.length !== stickyHeaders.length) return;

    const tableWidth = sourceTable.getBoundingClientRect().width;
    const headerHeight = sourceTable.tHead?.getBoundingClientRect().height || 0;
    const sourceHeadRect = sourceTable.tHead?.getBoundingClientRect();
    const tableRect = sourceTable.getBoundingClientRect();
    const stickyTop = Number.parseFloat(window.getComputedStyle(this.stickyHead).top) || 0;
    const stickyActive = sourceHeadRect
      && sourceHeadRect.top <= stickyTop
      && tableRect.bottom > stickyTop + headerHeight;
    this.stickyHead.style.height = `${headerHeight}px`;
    this.stickyHead.style.marginBottom = `-${headerHeight}px`;
    this.stickyHead.style.visibility = stickyActive ? 'visible' : 'hidden';
    this.stickyHead.style.pointerEvents = stickyActive ? 'auto' : 'none';
    this.stickyHeadViewport.style.width = `${this.scrollArea.clientWidth}px`;
    this.stickyTable.style.width = `${tableWidth}px`;
    this.stickyTable.style.transform = `translateX(${-this.scrollArea.scrollLeft}px)`;

    sourceHeaders.forEach((sourceHeader, index) => {
      const width = sourceHeader.getBoundingClientRect().width;
      stickyHeaders[index].style.width = `${width}px`;
      stickyHeaders[index].style.minWidth = `${width}px`;
      stickyHeaders[index].style.maxWidth = `${width}px`;
    });

    if (stickyHeaders[0]) stickyHeaders[0].style.transform = `translateX(${this.scrollArea.scrollLeft}px)`;
  }

  setMode(mode, { updateUrl, announce }) {
    const nextMode = mode === 'compare' ? 'compare' : 'grid';
    const compareActive = nextMode === 'compare';

    this.grid.hidden = compareActive;
    this.table.hidden = !compareActive;
    if (this.stickyHead) this.stickyHead.hidden = !compareActive;
    this.dataset.mode = nextMode;

    for (const button of this.buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.pjBulkMode === nextMode));
    }

    const catalog = this.closest('.pj-studio-collection__catalog');
    const skipLink = catalog?.querySelector('.skip-to-content');
    const gridList = this.grid.querySelector('[data-product-list]');
    if (skipLink) skipLink.href = compareActive ? `#${this.table.id}` : `#${gridList?.id || ''}`;

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('display');
      window.history.replaceState({}, '', url);
    }

    if (announce && this.status) {
      this.status.textContent = compareActive
        ? 'Compare view is active.'
        : 'Grid view is active.';
    }

    if (compareActive) window.requestAnimationFrame(() => this.syncStickyHeader());
  }
}

if (!customElements.get('pj-bulk-compare-view')) {
  customElements.define('pj-bulk-compare-view', PjBulkCompareView);
}
